import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import PasswordField from '../components/PasswordField';

export default function SetWellisPassword() {
  const { t } = useTranslation('app');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    // Redirect if user already has a Wellis password
    if (user.auth_method === 'email' || user.has_password) {
      navigate('/genealogy');
      return;
    }
  }, [authLoading, user, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = t('auth.setWellisPassword.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.setWellisPassword.passwordTooShort');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.setWellisPassword.passwordsMismatch');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.setWellisPassword.passwordsNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/set-wellis-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('auth.setWellisPassword.setError'));
      }

      setSuccess(t('auth.setWellisPassword.setSuccess'));
      setTimeout(() => {
        navigate(user.role === 'admin' ? '/admin' : '/genealogy');
      }, 1500);
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    setSkipped(true);
    setTimeout(() => {
      navigate(user?.role === 'admin' ? '/admin' : '/genealogy');
    }, 500);
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-body-md text-on-background/60">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-margin-mobile pt-24">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-headline-lg text-on-background mb-2">
            {t('auth.setWellisPassword.title')}
          </h1>
          <p className="text-body-md text-on-background/60">
            {t('auth.setWellisPassword.subtitle')}
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-6">
          <p className="text-body-sm text-on-background">
            <strong>💡 {t('auth.setWellisPassword.aboutWellisPassword')}</strong>
          </p>
          <ul className="text-body-xs text-on-background/80 mt-2 space-y-1 ml-4 list-disc">
            <li>{t('auth.setWellisPassword.separateFromGoogle')}</li>
            <li>{t('auth.setWellisPassword.accessIfLoseGoogle')}</li>
            <li>{t('auth.setWellisPassword.additionalSecurity')}</li>
            <li>{t('auth.setWellisPassword.changeInSettings')}</li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mb-6">
          {errors.submit && (
            <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
              {errors.submit}
            </div>
          )}

          {success && (
            <div className="bg-success/10 border border-success text-success p-4 rounded-lg mb-6 text-body-sm">
              {success}
            </div>
          )}

          <PasswordField
            label={t('auth.setWellisPassword.password')}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={t('auth.setWellisPassword.passwordPlaceholder')}
            error={errors.password}
            required
          />

          <PasswordField
            label={t('auth.setWellisPassword.confirmPassword')}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder={t('auth.setWellisPassword.confirmPasswordPlaceholder')}
            error={errors.confirmPassword}
            required
          />

          {/* Password Requirements */}
          <div className="bg-on-background/5 rounded-lg p-3 mb-6">
            <p className="text-body-xs font-medium text-on-background mb-2">
              {t('auth.setWellisPassword.requirements')}
            </p>
            <ul className="text-body-xs text-on-background/70 space-y-1 ml-4 list-disc">
              <li>{t('auth.setWellisPassword.minCharacters')}</li>
              <li>{t('auth.setWellisPassword.uppercaseLowercase')}</li>
              <li>{t('auth.setWellisPassword.numbersSymbols')}</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading || skipped}
            className="w-full bg-primary text-on-primary py-2 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? t('auth.setWellisPassword.setSetting') : t('auth.setWellisPassword.setButton')}
          </button>
        </form>

        {/* Skip Option */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSkip}
            disabled={loading || skipped}
            className="flex-1 bg-surface border border-outline text-on-background py-2 rounded-lg font-medium hover:bg-on-background/5 transition-colors disabled:opacity-50"
          >
            {skipped ? t('auth.setWellisPassword.skipping') : t('auth.setWellisPassword.skipButton')}
          </button>
        </div>

        {/* Help Text */}
        <p className="text-body-xs text-on-background/50 text-center mt-6">
          {t('auth.setWellisPassword.helpText')}
        </p>
      </div>
    </div>
  );
}
